import { useState, useEffect } from "react";
import { User, GraduationCap, Briefcase, FileCheck, ChevronRight, ChevronLeft, Menu, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import PersonalDataForm from "@/components/forms/PersonalDataForm";
import AcademicInfoForm from "@/components/forms/AcademicInfoForm";
import WorkExperienceForm from "@/components/forms/WorkExperienceForm";
import ConsentAndPreview from "@/components/forms/ConsentAndPreview";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8000").replace(/\/$/, "");
const API = `${BACKEND_URL}/api`;

const steps = [
  { id: 1, title: "Datos Personales", icon: User, description: "Información Básica" },
  { id: 2, title: "Información Académica", icon: GraduationCap, description: "Formación Y Estudios" },
  { id: 3, title: "Experiencia Laboral", icon: Briefcase, description: "Historial Profesional" },
  { id: 4, title: "Consentimiento Y CV", icon: FileCheck, description: "Revisión Final" },
];

export default function CVWizard() {
  const [currentStep, setCurrentStep] = useState(1);
  const [profileId, setProfileId] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Check for existing profile in localStorage
    const savedProfileId = localStorage.getItem("cv_profile_id");
    if (savedProfileId) {
      loadProfile(savedProfileId);
    }
  }, []);

  const loadProfile = async (id) => {
    try {
      const response = await axios.get(`${API}/profiles/${id}`);
      setProfile(response.data);
      setProfileId(id);
    } catch (error) {
      console.error("Error loading profile:", error);
      localStorage.removeItem("cv_profile_id");
    }
  };

  const createProfile = async () => {
    try {
      const response = await axios.post(`${API}/profiles`, {});
      setProfileId(response.data.id);
      setProfile(response.data);
      localStorage.setItem("cv_profile_id", response.data.id);
      return response.data.id;
    } catch (error) {
      console.error("Error creating profile:", error);
      toast.error("Error Al Crear El Perfil");
      return null;
    }
  };

  const updateProfile = async (data) => {
    let currentId = profileId;
    if (!currentId) {
      const newId = await createProfile();
      if (!newId) return null;
      currentId = newId;
    }

    try {
      setLoading(true);
      const response = await axios.put(`${API}/profiles/${currentId}`, data);
      setProfile(response.data);
      return response.data;
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Error Al Actualizar El Perfil");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
      setMobileMenuOpen(false);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setMobileMenuOpen(false);
    }
  };

  const goToStep = (step) => {
    if (step > currentStep) {
      return;
    }
    setCurrentStep(step);
    setMobileMenuOpen(false);
  };

  const progress = (currentStep / 4) * 100;

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <PersonalDataForm
            profile={profile}
            profileId={profileId}
            createProfile={createProfile}
            updateProfile={updateProfile}
            onNext={handleNext}
            loading={loading}
          />
        );
      case 2:
        return (
          <AcademicInfoForm
            profile={profile}
            profileId={profileId}
            setProfile={setProfile}
            onNext={handleNext}
            onPrev={handlePrev}
            loading={loading}
          />
        );
      case 3:
        return (
          <WorkExperienceForm
            profile={profile}
            profileId={profileId}
            setProfile={setProfile}
            onNext={handleNext}
            onPrev={handlePrev}
            loading={loading}
          />
        );
      case 4:
        return (
          <ConsentAndPreview
            profile={profile}
            profileId={profileId}
            updateProfile={updateProfile}
            onPrev={handlePrev}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="cv-wizard">
      {/* Mobile Header */}
      <div className="lg:hidden bg-black text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-yellow-400 rounded flex items-center justify-center">
            <img
              src="/logo.png"
              alt="Logo De La Empresa"
              className="w-full h-full object-contain"
            />
          </div>
          <div>
            <h1 className="font-oswald text-lg font-bold tracking-wide">Fundación Forjando Futuros</h1>
            <p className="text-xs text-gray-400">Paso {currentStep} De 4</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="text-white hover:bg-gray-800"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black bg-opacity-90 mobile-menu-enter">
          <div className="p-6">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-white font-oswald text-xl font-bold">Navegación</h2>
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-gray-800"
                onClick={() => setMobileMenuOpen(false)}
              >
                <X className="w-6 h-6" />
              </Button>
            </div>
            <nav className="space-y-4">
              {steps.map((step) => (
                <button
                  key={step.id}
                  type="button"
                  disabled
                  className={`w-full flex items-center gap-4 p-4 rounded transition-all disabled:opacity-100 disabled:cursor-default ${
                    currentStep === step.id
                      ? "bg-yellow-400 text-black"
                      : currentStep > step.id
                      ? "bg-gray-800 text-white"
                      : "bg-gray-900 text-gray-400"
                  }`}
                  data-testid={`mobile-step-${step.id}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    currentStep === step.id
                      ? "bg-black text-yellow-400"
                      : currentStep > step.id
                      ? "bg-green-500 text-white"
                      : "bg-gray-700 text-gray-400"
                  }`}>
                    {currentStep > step.id ? <Check className="w-5 h-5" /> : step.id}
                  </div>
                  <div className="text-left">
                    <p className="font-semibold">{step.title}</p>
                    <p className="text-sm opacity-70">{step.description}</p>
                  </div>
                </button>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:w-[35%] cv-forge-sidebar text-white flex-col p-8 min-h-screen">
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 bg-yellow-400 rounded flex items-center justify-center">
              <img
                src="/logo.png"
                alt="Logo De La Empresa"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <h1 className="font-oswald text-2xl font-bold tracking-wider">Fundación Forjando Futuros</h1>
              <p className="text-gray-400 text-sm">Plataforma De Registro De Hojas De Vida</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 space-y-2">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;

            return (
              <button
                key={step.id}
                type="button"
                disabled
                className={`w-full flex items-center gap-4 p-4 rounded-sm transition-all step-indicator disabled:opacity-100 disabled:cursor-default ${
                  isActive ? "active" : ""
                }`}
                data-testid={`sidebar-step-${step.id}`}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg transition-all ${
                    isActive
                      ? "bg-yellow-400 text-black"
                      : isCompleted
                      ? "bg-green-500 text-white"
                      : "bg-gray-700 text-gray-400"
                  }`}
                >
                  {isCompleted ? <Check className="w-5 h-5" /> : step.id}
                </div>
                <div className="text-left flex-1">
                  <p
                    className={`font-semibold transition-colors ${
                      isActive
                        ? "text-yellow-400"
                        : isCompleted
                        ? "text-white"
                        : "text-gray-500"
                    }`}
                  >
                    {step.title}
                  </p>
                  <p className="text-sm text-gray-500">{step.description}</p>
                </div>
                {isActive && (
                  <ChevronRight className="w-5 h-5 text-yellow-400" />
                )}
              </button>
            );
          })}
        </nav>

        <div className="mt-auto pt-8 border-t border-gray-800">
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-400">Progreso</span>
              <span className="text-yellow-400 font-semibold">{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2 bg-gray-800" />
          </div>
          <p className="text-xs text-gray-500 text-center">
            Todos Los Datos Son Tratados De Forma Confidencial
          </p>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 bg-white min-h-screen">
        <div className="lg:hidden px-4 py-3 bg-gray-50 border-b">
          <Progress value={progress} className="h-2" />
        </div>
        
        <div className="p-6 lg:p-12 max-w-3xl mx-auto">
          {renderStepContent()}
        </div>
      </main>
    </div>
  );
}
